import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import styles from './HeaderAuth.module.css'
import { BasicStyledComponent } from 'shared-definitions/types'
import { AuthContext } from '../contexts/AuthContext'
import clsx from 'clsx'
import RWButton from '../buttons/RWButton'
import ArrowBird from 'shared-svg/ArrowBottomBird.svg'
import Person from 'shared-svg/PersonHollow.svg'
import { useOutsideClick } from '../hooks/use-outside-click'
import { useGlobalKey } from '../hooks/use-global-key'
import { useNotifications } from '../hooks/use-notifications'
import { googleSignInIntent } from '../lib/google-sign-in-intent'
import AuthMenu, {
  type AuthMenuEntry,
  type AuthMenuIdentity,
  type AuthMenuItem,
  type NotificationLinks,
} from './AuthMenu'

/**
 * The account control doubles as the notification surface.
 *
 * There is no separate bell: the header already carries one identity affordance
 * on every page, and a second icon beside it competed with it for the same
 * meaning. Unread count rides the avatar; the list lives one click in, inside
 * the account menu.
 *
 * `useNotifications` is called here rather than higher up because this component
 * renders only for a signed-in reader — so the 60s poller is never mounted for
 * an anonymous one, which is the contract's "zero requests" rule expressed as
 * structure instead of a condition someone can later delete.
 */
/**
 * Whether focus sits inside the menu — which `visibility: hidden` is about to take out of the
 * page, dropping focus to <body>. Then closing hands focus back to the account button. Pure.
 */
export function menuHoldsFocus(
  menu: Pick<Node, 'contains'> | null,
  active: Element | null
): boolean {
  return Boolean(menu && active && menu.contains(active))
}

interface HeaderAuthProps extends BasicStyledComponent {
  /** Rows between Notifications and Settings when there are no `primaryItems`; Android Authority
   *  passes none. */
  menuItems?: ReadonlyArray<AuthMenuItem>
  /** The app's own notifications page (SoundGuys' member hub); absent = the tray's inline
   *  settings. See `NotificationLinks`. */
  notificationLinks?: NotificationLinks
  /** Each time the account menu opens — an app resolves `notificationLinks` lazily here rather
   *  than on every page view. */
  onMenuOpen?: () => void
  /** Optional: the reader is about to open the menu (hover, focus, touch) — a chance to prefetch
   * what the menu shows. Additive; Android Authority passes nothing. */
  onMenuIntent?: () => void
  /** The reader's own avatar in place of the generic person icon (both apps: commentHub picture or
   *  initials); the unread badge rides it all the same. Absent = the icon, unchanged. */
  avatar?: ReactNode
  /** The menu's identity header and rows — see `AuthMenu`'s `identity` / `primaryItems` /
   *  `quietItems`. */
  identity?: AuthMenuIdentity
  primaryItems?: ReadonlyArray<AuthMenuEntry>
  quietItems?: ReadonlyArray<AuthMenuItem>
  /** Optional: the badge's unread count each time it changes, and 0 when the reader signs out —
   *  so an app can show the same figure elsewhere without a second poller (SoundGuys' Community
   *  pill). Additive; Android Authority passes nothing. */
  onUnreadChange?: (unread: number, capped: boolean) => void
}

const HeaderAuthLoggedIn: FC<HeaderAuthProps> = ({
  className,
  menuItems,
  notificationLinks,
  onMenuOpen,
  onMenuIntent,
  avatar,
  identity,
  primaryItems,
  quietItems,
  onUnreadChange,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const notifications = useNotifications()
  const reportUnread = useRef(onUnreadChange)
  reportUnread.current = onUnreadChange
  useEffect(() => {
    reportUnread.current?.(notifications.unread, notifications.capped)
  }, [notifications.unread, notifications.capped])
  // Signed out = this component unmounts: the figure goes back to 0.
  useEffect(() => () => reportUnread.current?.(0, false), [])
  const ref = useOutsideClick<HTMLDivElement>(() => {
    setIsMenuOpen(false)
  }, [])
  const buttonRef = useRef<HTMLButtonElement>(null)
  // Escape and a menu action (Dark mode, a row): the closed menu is `visibility: hidden`, so focus
  // left inside it would fall to <body> — it goes back to the account button. An outside click
  // keeps plain close: the reader put focus where they clicked.
  const closeMenu = (): void => {
    if (menuHoldsFocus(ref.current, document.activeElement)) {
      buttonRef.current?.focus()
    }
    setIsMenuOpen(false)
  }
  useGlobalKey({ code: 'Escape' }, () => {
    // Every Escape on the page lands here (the login dialog, a comment composer): only one that
    // closes this open menu may move focus.
    if (isMenuOpen) {
      closeMenu()
    }
  })

  const unreadLabel = notifications.capped ? '99+' : String(notifications.unread)

  return (
    <div className={clsx(className, styles.loggedIn)} ref={ref}>
      <button
        ref={buttonRef}
        className={clsx(styles.loggedInMenuButton, { [styles.loggedInMenuButtonAvatar]: avatar })}
        type="button"
        onPointerEnter={onMenuIntent}
        onFocus={onMenuIntent}
        onTouchStart={onMenuIntent}
        aria-label={
          notifications.unread > 0 ? `Account, ${unreadLabel} unread notifications` : 'Account'
        }
        onClick={() => {
          if (!isMenuOpen) {
            onMenuOpen?.()
          }
          setIsMenuOpen(s => !s)
        }}
      >
        <span className={styles.avatarWrap}>
          {avatar ? (
            <span
              className={clsx(styles.avatar, {
                [styles.avatarAlerted]: notifications.unread > 0,
              })}
              aria-hidden="true"
            >
              {avatar}
            </span>
          ) : (
            <Person
              className={clsx(styles.loggedInIcon, {
                [styles.loggedInIconAlerted]: notifications.unread > 0,
              })}
            />
          )}
          {notifications.unread > 0 ? (
            // `capped` is the server's flag; the number is already clamped by
            // the time it reaches us, so this never compares it itself.
            <span className={styles.badge}>{unreadLabel}</span>
          ) : null}
        </span>
        <ArrowBird
          className={clsx(styles.loggedInArrow, {
            [styles.loggedInArrowOpened]: isMenuOpen,
            // With an avatar the caret sits beside it, not over its corner (additive: no avatar
            // keeps the absolute caret and identical markup).
            [styles.loggedInArrowInline]: avatar,
          })}
        />
      </button>

      <AuthMenu
        items={menuItems}
        notifications={notifications}
        menuOpen={isMenuOpen}
        notificationLinks={notificationLinks}
        identity={identity}
        primaryItems={primaryItems}
        quietItems={quietItems}
        onActionComplete={closeMenu}
        className={clsx(styles.loggedInMenu, { [styles.loggedInMenuOpened]: isMenuOpen })}
      />
    </div>
  )
}

interface Clickable {
  onClick?: () => void
  /** Hover / press / focus on Login starts the Google script download (`prewarmGoogle`). */
  onIntent?: () => void
}

const HeaderAuthLoggedOut: FC<Clickable & BasicStyledComponent> = ({
  onClick,
  onIntent,
  className,
}) => (
  <RWButton
    className={clsx(className, styles.loggedOutButton)}
    onClick={onClick}
    {...googleSignInIntent(onIntent)}
    variant="secondary"
    size="medium"
  >
    {'Login'}
  </RWButton>
)

const HeaderAuth: FC<HeaderAuthProps> = ({ className, ...menu }) => {
  const { showLogIn, prewarmGoogle, authState } = AuthContext.useContainer()

  return authState ? (
    <HeaderAuthLoggedIn className={className} {...menu} />
  ) : (
    <HeaderAuthLoggedOut className={className} onClick={showLogIn} onIntent={prewarmGoogle} />
  )
}

export default HeaderAuth
